// Public API Service for Landing Page
// No authentication required for these endpoints

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class PublicApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, params = {}) {
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();

    const url = `${this.baseURL}${endpoint}${queryString ? `?${queryString}` : ''}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Public API Request Error:', error);
      throw error;
    }
  }

  // Get all categories with products count for landing page
  async getCategories(region) {
    return this.request('/public/categories', region ? { region } : {});
  }

  // Get categories with their products (for "Nos Offres Spéciales" section)
  async getCategoriesWithProducts(region) {
    return this.request('/public/categories-with-products', region ? { region } : {});
  }

  // Get products with promotions (for "Produits en Promotion" section)
  async getProductsWithPromotion(region) {
    return this.request('/public/products-with-promotion', region ? { region } : {});
  }

  // Get best selling products (for "Meilleures Ventes" section)
  async getBestSellers() {
    return this.request('/public/best-sellers');
  }

  // Get products by category ID (for "Ramadanesque" section, etc.)
  async getProductsByCategory(categoryId) {
    return this.request(`/public/products/category/${categoryId}`);
  }

  // Get ALL products (for "Nouvelles product" section)
  async getAllProducts() {
    return this.request('/public/all-products');
  }

  // Get products for landing page
  async getProducts(params = {}) {
    return this.request('/public/products', params);
  }

  // Get featured products for ProductListOne section (6 products, changes daily)
  async getFeaturedProducts() {
    return this.request('/public/featured-products');
  }

  // Get active offers for landing page
  async getOffers() {
    return this.request('/public/offers');
  }

  // Get site settings (header, footer, general)
  async getSettings() {
    return this.request('/public/settings');
  }

  // Get single product details for product page
  async getProduct(productId, params = {}) {
    return this.request(`/public/products/${productId}`, params);
  }

  // Get product reviews
  async getProductReviews(productId) {
    return this.request(`/public/products/${productId}/reviews`);
  }

  // Get related products (same category)
  async getRelatedProducts(productId) {
    return this.request(`/public/products/${productId}/related`);
  }

  // Submit a review (requires auth token)
  async submitReview(data, token) {
    const url = `${this.baseURL}/reviews`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Submit Review Error:', error);
      throw error;
    }
  }

  // Reply to a review (requires auth token)
  async replyToReview(reviewId, comment, token) {
    const url = `${this.baseURL}/reviews/${reviewId}/reply`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ comment }),
      });
      return await response.json();
    } catch (error) {
      console.error('Reply Error:', error);
      throw error;
    }
  }

  // Like a review
  async likeReview(reviewId) {
    const url = `${this.baseURL}/reviews/${reviewId}/like`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      return await response.json();
    } catch (error) {
      console.error('Like Error:', error);
      throw error;
    }
  }

  // Get all active packs with product images for landing page
  async getPacks() {
    return this.request('/public/packs');
  }

  // Get storage URL for images
  getImageUrl(imagePath) {
    if (!imagePath) return null;
    const normalizedPath = String(imagePath).trim();
    if (!normalizedPath) return null;

    // Keep absolute URLs and local frontend assets untouched.
    if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
      return normalizedPath;
    }
    if (normalizedPath.startsWith('/assets/') || normalizedPath.startsWith('assets/')) {
      return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
    }

    const storageBaseUrl = this.baseURL.replace('/api', '');

    // Support all common backend formats without duplicating /storage.
    if (normalizedPath.startsWith('/storage/')) {
      return `${storageBaseUrl}${normalizedPath}`;
    }
    if (normalizedPath.startsWith('storage/')) {
      return `${storageBaseUrl}/${normalizedPath}`;
    }
    if (normalizedPath.startsWith('/')) {
      return `${storageBaseUrl}${normalizedPath}`;
    }

    return `${storageBaseUrl}/storage/${normalizedPath}`;
  }
}

export default new PublicApiService();
